import React, { useContext, useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import {
  Row,
  Col,
  Card,
  Table,
  Badge,
  Button,
  Form,
  Modal,
} from "react-bootstrap";
import {
  Shield,
  CheckCircle,
  XCircle,
  Search,
  Plus,
  MoreVertical,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import AdminMasterLayout from "../../../layouts/admin/AdminMasterLayout ";
import "../../../assets/styles/admin/AdminSubAdmins.css";
import axios from "axios";
import Swal from "sweetalert2";
import AppContext from "../../../context/AppContext";

/* =====================================================
   CUSTOM PORTAL DROPDOWN
   ===================================================== */
const ActionMenu = ({ admin, onEdit, onToggleSuspend, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const handleToggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + window.scrollY + 4,
        left: rect.right + window.scrollX - 168,
      });
    }
    setOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        btnRef.current &&
        !btnRef.current.contains(e.target)
      )
        setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleScroll = () => setOpen(false);
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [open]);

  const menu = open
    ? ReactDOM.createPortal(
        <div
          ref={menuRef}
          className="sa-portal-menu"
          style={{ top: menuPos.top, left: menuPos.left }}
        >
          <button
            className="sa-portal-item"
            onClick={() => {
              setOpen(false);
              onEdit(admin);
            }}
          >
            Edit
          </button>
          <button
            className="sa-portal-item"
            onClick={() => {
              setOpen(false);
              onToggleSuspend(admin);
            }}
          >
            {admin.status === "active" ? "Suspend" : "Activate"}
          </button>
          <div className="sa-portal-divider" />
          <button
            className="sa-portal-item danger"
            onClick={() => {
              setOpen(false);
              onDelete(admin);
            }}
          >
            Delete
          </button>
        </div>,
        document.body,
      )
    : null;

  return (
    <>
      <button
        ref={btnRef}
        className="admin-subadmins-dropdown-toggle"
        onClick={handleToggle}
      >
        <MoreVertical size={18} />
      </button>
      {menu}
    </>
  );
};

/* =====================================================
   MAIN COMPONENT
   ===================================================== */
const AdminSubAdmins = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [textForEdit, setTextForEdit] = useState("Add");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRole, setSelectedRole] = useState("All Roles");
  const [subAdmins, setSubAdmins] = useState([]);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("Moderator");
  const [editingAdminId, setEditingAdminId] = useState(null);

  const { baseUrl } = useContext(AppContext);

  const statsData = [
    {
      id: 1,
      title: "Total Admins",
      value: "5",
      icon: Shield,
      iconBg: "#dbeafe",
      iconColor: "#2563eb",
    },
    {
      id: 2,
      title: "Active",
      value: "4",
      icon: CheckCircle,
      iconBg: "#d1fae5",
      iconColor: "#059669",
    },
    {
      id: 3,
      title: "Suspended",
      value: "1",
      icon: XCircle,
      iconBg: "#fee2e2",
      iconColor: "#dc2626",
    },
  ];

  const handleShowModal = () => {
    setTextForEdit("Add");
    setEditingAdminId(null);
    setFullName("");
    setEmail("");
    setPassword("");
    setRole("Moderator");
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setTextForEdit("Add");
    setEditingAdminId(null);
    setFullName("");
    setEmail("");
    setPassword("");
    setRole("Moderator");
  };

  const viewAllAdmins = () => {
    axios
      .get(`${baseUrl}/admin/auth/viewAllAdmins`)
      .then((response) => {
        if (response.data != null) setSubAdmins(response.data.admins);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  /* ✅ FIX: Add aur Edit ke liye alag API, URL mein koi typo nahi */
  const handleSave = (e) => {
    e.preventDefault();
    console.log(role);

    const formData = new FormData();
    
    formData.append("fullName", fullName);
    formData.append("email", email);
    formData.append("isSuper", role);
    if (password) formData.append("password", password);

    if (textForEdit === "Edit") {
      // ── EDIT ──
      formData.append("mwadmin_id", editingAdminId);

      axios
        .post(
          `${baseUrl}/admin/auth/updateAdmin`, // ✅ clean URL, no typo
          formData,
          { headers: { "Content-Type": "multipart/form-data" } },
        )
        .then((response) => {
          console.log("update response:", response.data);
          Swal.fire({
            title: "Updated!",
            text: "Admin updated successfully",
            icon: "success",
            confirmButtonText: "OK",
          });
          viewAllAdmins();
        })
        .catch((error) => {
          console.log("update error:", error);
          Swal.fire({
            title: "Error!",
            text: error?.response?.data?.message || "Could not update admin",
            icon: "error",
            confirmButtonText: "OK",
          });
        });
    } else {
      // ── ADD ──
      // ✅ FIX: pehle `(${baseUrl}/...` tha — extra '(' URL tod raha tha
      axios
        .post(
          `${baseUrl}/admin/auth/createAdmin`, // ✅ correct URL
          formData,
          { headers: { "Content-Type": "multipart/form-data" } },
        )
        .then((response) => {
          console.log("create response:", response.data);
          Swal.fire({
            title: "Congratulations!",
            text: "Admin added successfully",
            icon: "success",
            confirmButtonText: "Cool",
          });
          viewAllAdmins();
        })
        .catch((error) => {
          console.log("create error:", error);
          Swal.fire({
            title: "Error!",
            text: error?.response?.data?.message || "Could not add admin",
            icon: "error",
            confirmButtonText: "Try Again",
          });
        });
    }

    handleCloseModal();
  };

  const editAdmin = (adminForEdit) => {
    setTextForEdit("Edit");
    setEditingAdminId(adminForEdit.id);
    setFullName(adminForEdit.fullName);
    setEmail(adminForEdit.email);
    setRole(adminForEdit.isSuper || "Moderator");
    setPassword("");
    setShowAddModal(true);
  };

  const toggleSuspend = (admin) => {
    // console.log("status changed", admin.status);
    const newStatus = admin.status === "active" ? "suspended" : "active";
    Swal.fire({
      title: `${newStatus === "suspended" ? "Suspend" : "Activate"} Admin?`,
      text: `Are you sure you want to ${newStatus === "suspended" ? "suspend" : "activate"} ${admin.fullName}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        formData.append("mwadmin_id", admin.id);
        // formData.append("status", newStatus);
        axios
          .post(`${baseUrl}/admin/auth/updateAdminStatus`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          })
          .then((response) => {
            console.log(response.data)
            viewAllAdmins();
            Swal.fire("Done!", `Admin has been ${newStatus}.`, "success");
          })
          .catch(() => {
            setSubAdmins((prev) =>
              prev.map((a) =>
                a.id === admin.id ? { ...a, status: newStatus } : a,
              ),
            );
          });
      }
    });
  };

  const deleteAdmin = (admin) => {
    console.log("deleteAdmin", admin.id);
    Swal.fire({
      title: "Delete Admin?",
      text: `Are you sure you want to delete ${admin.fullName}?`,
      icon: "error",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#dc2626",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        formData.append("mwadmin_id", admin.id);
        axios
          .post(`${baseUrl}/admin/auth/deleteAdmin`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          })
          .then((response) => {
            console.log(response.data)
            viewAllAdmins();
            Swal.fire("Deleted!", "Admin has been deleted.", "success");
          })
          .catch(() => {
            setSubAdmins((prev) => prev.filter((a) => a.id !== admin.id));
          });
      }
    });
  };

  useEffect(() => {
    viewAllAdmins();
  }, []);

  return (
    <AdminMasterLayout>
      <div className="admin-subadmins-wrapper">
        {/* Header */}
        <div className="admin-subadmins-header">
          <div>
            <h1 className="admin-subadmins-title">Sub-Admin Management</h1>
            <p className="admin-subadmins-subtitle">
              Manage platform administrators and moderators
            </p>
          </div>
          <Button className="admin-subadmins-add-btn" onClick={handleShowModal}>
            <Plus size={18} strokeWidth={2.5} /> Add Sub-Admin
          </Button>
        </div>

        {/* Stats */}
        <Row className="admin-subadmins-stats-row g-3 g-lg-4">
          {statsData.map((stat, index) => (
            <Col xs={12} sm={6} lg={4} key={stat.id}>
              <Card
                className="admin-subadmins-stat-card"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <Card.Body>
                  <div className="admin-subadmins-stat-content">
                    <div
                      className="admin-subadmins-stat-icon-box"
                      style={{ backgroundColor: stat.iconBg }}
                    >
                      <stat.icon
                        size={24}
                        style={{ color: stat.iconColor }}
                        strokeWidth={2}
                      />
                    </div>
                    <div className="admin-subadmins-stat-info">
                      <span className="admin-subadmins-stat-label">
                        {stat.title}
                      </span>
                      <h3 className="admin-subadmins-stat-value">
                        {stat.value}
                      </h3>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Search & Filter */}
        <Card className="admin-subadmins-filter-card">
          <Card.Body>
            <Row className="g-3 align-items-center">
              <Col xs={12} lg={8}>
                <div className="admin-subadmins-search-wrapper">
                  <Search size={18} className="admin-subadmins-search-icon" />
                  <Form.Control
                    type="text"
                    placeholder="Search admins..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="admin-subadmins-search-input"
                    style={{
                      paddingLeft: "48px",
                      paddingRight: "16px",
                      height: "46px",
                    }}
                  />
                </div>
              </Col>
              <Col xs={12} lg={4}>
                <Form.Select
                  className="admin-subadmins-role-filter"
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                >
                  <option>All Roles</option>
                  <option>Super Admin</option>
                  <option>Moderator</option>
                </Form.Select>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Table */}
        <Card className="admin-subadmins-table-card">
          <Card.Body>
            <div className="admin-subadmins-table-wrapper">
              <Table className="admin-subadmins-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {subAdmins.map((admin, index) => (
                    <tr
                      key={admin.id}
                      style={{ animationDelay: `${index * 0.05}s` }}
                    >
                      <td>
                        <span className="admin-subadmins-name">
                          {admin.fullName}
                        </span>
                      </td>
                      <td>
                        <span className="admin-subadmins-email">
                          {admin.email}
                        </span>
                      </td>
                      <td>
                        <Badge
                          bg={
                            admin.isSuper === "Super Admin"
                              ? "primary"
                              : "success"
                          }
                          className="admin-subadmins-role-badge"
                        >
                          {admin.isSuper}
                        </Badge>
                      </td>
                      <td>
                        <Badge
                          bg={admin.status === "active" ? "primary" : "danger"}
                          className="admin-subadmins-status-badge"
                        >
                          {admin.status}
                        </Badge>
                      </td>
                      <td>
                        <span className="admin-subadmins-date">
                          {admin.created_at}
                        </span>
                      </td>
                      <td>
                        <ActionMenu
                          admin={admin}
                          onEdit={editAdmin}
                          onToggleSuspend={toggleSuspend}
                          onDelete={deleteAdmin}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {/* Pagination */}
            <div className="admin-subadmins-pagination">
              <span className="admin-subadmins-pagination-info">
                Showing 1-5 of 5
              </span>
              <div className="admin-subadmins-pagination-controls">
                <Button className="admin-subadmins-pagination-btn" disabled>
                  <ChevronLeft size={18} />
                </Button>
                <span className="admin-subadmins-page-info">Page 1 of 1</span>
                <Button className="admin-subadmins-pagination-btn" disabled>
                  <ChevronRight size={18} />
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* Add / Edit Modal */}
        <Modal
          show={showAddModal}
          onHide={handleCloseModal}
          centered
          className="admin-subadmins-modal"
        >
          <Modal.Header closeButton className="admin-subadmins-modal-header">
            <Modal.Title className="admin-subadmins-modal-title">
              {textForEdit} Sub-Admin
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="admin-subadmins-modal-body">
            <Form>
              <Form.Group className="admin-subadmins-form-group">
                <Form.Label className="admin-subadmins-form-label">
                  Full Name
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter full name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="admin-subadmins-form-input"
                  required
                />
              </Form.Group>

              <Form.Group className="admin-subadmins-form-group">
                <Form.Label className="admin-subadmins-form-label">
                  Email
                </Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="admin-subadmins-form-input"
                  required
                />
              </Form.Group>

              <Form.Group className="admin-subadmins-form-group">
                <Form.Label className="admin-subadmins-form-label">
                  Password{" "}
                  {textForEdit === "Edit" && (
                    <span
                      style={{
                        fontWeight: 400,
                        color: "#9ca3af",
                        fontSize: "12px",
                      }}
                    >
                      (leave blank to keep current)
                    </span>
                  )}
                </Form.Label>
                <Form.Control
                  type="password"
                  placeholder={
                    textForEdit === "Edit"
                      ? "Leave blank to keep current"
                      : "Enter password"
                  }
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="admin-subadmins-form-input"
                  required={textForEdit === "Add"}
                />
              </Form.Group>

              <Form.Group className="admin-subadmins-form-group">
                <Form.Label className="admin-subadmins-form-label">
                  Role
                </Form.Label>
                <Form.Select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="admin-subadmins-form-select"
                  required
                >
                  <option value="Moderator">Moderator</option>
                  <option value="Super Admin">Super Admin</option>
                </Form.Select>
              </Form.Group>

              <div className="admin-subadmins-modal-footer">
                <Button
                  type="button"
                  className="admin-subadmins-save-btn"
                  onClick={handleSave}
                >
                  {textForEdit === "Edit" ? "Update" : "Save"}
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      </div>
    </AdminMasterLayout>
  );
};

export default AdminSubAdmins;
